import { useMemo, useRef, useState } from 'react';
import { Form, ListGroup, Overlay } from 'react-bootstrap';

/**
 * Free-text description that suggests the org's items as you type. Picking one links the line to that item
 * (onPick gets the item); typing over it unlinks it again (onChange gets the text). Anything not in the list,
 * such as freight or labour, can still be typed in.
 * items: [{ itemId, name, sku, hsnCode, unitOfMeasurementId, unitSymbol }]
 */
function ItemDescriptionInput({ value, itemId, items = [], onChange, onPick, maxLength = 255, id }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  const matches = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    const found = q
      ? items.filter((i) => `${i.name} ${i.sku || ''} ${i.hsnCode || ''}`.toLowerCase().includes(q))
      : items;
    return found.slice(0, 20);
  }, [items, value]);

  const show = open && matches.length > 0 && !itemId;

  const pick = (item) => {
    onPick(item);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!show) {
      if (e.key === 'ArrowDown' && !itemId) setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(matches[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <>
      <Form.Control
        ref={inputRef}
        id={id}
        size="sm"
        value={value}
        maxLength={maxLength}
        autoComplete="off"
        placeholder={items.length > 0 ? 'Type to pick an item, or describe it' : ''}
        onChange={(e) => { onChange(e.target.value); setHighlight(0); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {itemId && <div className="small text-success mt-1">Linked to item</div>}
      <Overlay target={inputRef} show={show} placement="bottom-start">
        {({ placement, arrowProps, show: _show, popper, hasDoneInitialMeasure, style, ...props }) => (
          <div {...props} style={{ ...style, zIndex: 1080, minWidth: inputRef.current?.offsetWidth || 200 }}>
            <ListGroup className="shadow-sm" style={{ maxHeight: 240, overflowY: 'auto' }}>
              {matches.map((item, idx) => (
                <ListGroup.Item
                  key={item.itemId}
                  action
                  active={idx === highlight}
                  className="py-1 small"
                  // Keep focus on the input so its blur doesn't close the list before the pick lands
                  onMouseDown={(e) => { e.preventDefault(); pick(item); }}
                  onMouseEnter={() => setHighlight(idx)}
                >
                  <div>{item.name}</div>
                  {(item.sku || item.hsnCode || item.unitSymbol) && (
                    <div className={idx === highlight ? '' : 'text-muted'}>
                      {[item.sku, item.hsnCode && `HSN ${item.hsnCode}`, item.unitSymbol].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}
      </Overlay>
    </>
  );
}

/**
 * Lines of the ticked GRNs, merged into the lines already on the form: lines from a GRN that was unticked go,
 * lines of a newly ticked GRN are added, and blank lines give way to them. toLine shapes a GRN line into a form line.
 */
export function mergeGrnLines(lines, grns, selectedIds, toLine, emptyLine) {
  const kept = lines.filter((l) => l.fromGrn == null || selectedIds.includes(l.fromGrn));
  const present = new Set(kept.map((l) => l.fromGrn).filter((x) => x != null));
  const added = grns
    .filter((g) => selectedIds.includes(g.id) && !present.has(g.id))
    .flatMap((g) => (g.lines || []).map((gl) => ({ ...toLine(gl), fromGrn: g.id })));
  const blank = JSON.stringify(emptyLine());
  const merged = [...kept.filter((l) => !(added.length > 0 && JSON.stringify(l) === blank)), ...added];
  return merged.length > 0 ? merged : [emptyLine()];
}

export default ItemDescriptionInput;
